import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { Breadcrumbs } from '@components/navigation/Breadcrumb.tsx';
import SupplierLayout from '@components/layout/supplier/SupplierLayout.tsx';
import SupplierProductDetailPage from '@features/supplier/products/SupplierProductDetailPage.tsx';
import { useSupplierProductDetailStore } from '@features/supplier/products/supplierProductDetailStore.ts';
import { useTranslation } from 'react-i18next';

const searchSchema = z.object({
  tab: z.enum(['customers', 'portals', 'prices', 'auctions']).optional(),
  page: z.coerce.number().optional(),
  size: z.coerce.number().optional(),
  // Filtry pro ceny
  organizationId: z.coerce.number().optional(),
  auctionId: z.coerce.number().optional(),
  guaranteedFilter: z.enum(['GUARANTEED', 'NOT_GUARANTEED', 'ALL']).optional(),
  priceType: z.enum(['STANDARD', 'ACTION_PRICE', 'SUBSTITUTE_PRICE', 'FROM_DL_PRICE']).optional(),
  // Vyhledávání pro zákazníky/aukce
  q: z.string().optional(),
});

export type SupplierProductDetailSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/s/$orgId/$portalId/products/$productId')({
  validateSearch: search => searchSchema.parse(search),
  component: RouteComponent,
  beforeLoad: async ({ params, search }) => {
    const supplierId = Number(params.orgId);
    const portalId = Number(params.portalId);
    const productId = Number(params.productId);
    const store = useSupplierProductDetailStore.getState();

    const tab = search.tab || 'customers';
    const page = search.page || 1;
    const size = search.size || 20;
    const { organizationId, auctionId, guaranteedFilter, priceType, q } = search;

    // Načti základní data paralelně
    await Promise.all([
      store.loadProductDetail(supplierId, portalId, productId),
      store.loadImages(supplierId, portalId, productId),
      store.loadCustomers(supplierId, portalId, productId, tab === 'customers' ? q : undefined),
      store.loadAuctions(supplierId, portalId, productId, tab === 'auctions' ? q : undefined),
      store.loadPortals(supplierId, portalId, productId),
      store.loadPrices(
        supplierId,
        portalId,
        productId,
        tab === 'prices' ? { page, size, organizationId, auctionId, guaranteedFilter, priceType } : { page: 1, size: 20 },
      ),
    ]);
  },
});

function RouteComponent() {
  const { t } = useTranslation('client');
  const { productId, orgId, portalId } = Route.useParams();

  return (
    <SupplierLayout>
      <div className="mb-8">
        <Breadcrumbs
          items={[
            { label: t('supplierCatalog.breadcrumb'), href: `/s/${orgId}/${portalId}/catalog` },
            { label: t('supplierCatalog.productDetailBreadcrumb'), href: undefined },
          ]}
        />
      </div>

      <SupplierProductDetailPage productId={Number(productId)} />
    </SupplierLayout>
  );
}
